import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {RecodeCurrencyFormatter} from '@fantasmagames/core/src/utils/RecodeCurrencyFormatter';
import {
	IRecodeAutoPlayConfig,
	IRecodeAutoPlayExtraConfig,
	IRecodeBet,
	RecodeIntegrator,
} from '@fantasmagames/slot/src/integration/RecodeIntegrator';
import {ReCodeStorage} from '@fantasmagames/slot/src/integration/ReCodeStorage';
import {Action} from '@fantasmagames/slot/src/logic/Action';
import {Communicator} from '@fantasmagames/slot/src/logic/Communicator';
import {FreeGamesBetData, FreeGamesData, FreeGamesInProgress} from '@fantasmagames/slot/src/logic/integration/FreeGamesData';
import {RecodeLogicEvents} from '@fantasmagames/slot/src/logic/integration/RecodeLogicEvents';
import {RecodeSpinResult} from '@fantasmagames/slot/src/logic/integration/RecodeSpinResult';
import {ISymbolValue, Logic} from '@fantasmagames/slot/src/logic/Logic';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {SpinResult} from '@fantasmagames/slot/src/logic/SpinResult';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';
import {Device} from '@fantasmagames/core/src/device/Device';

export enum RoundTypes {
	BASE_SPIN = 0,
	WILD_EXPANSION = 1,
	SCATTER_REPLACEMENT = 2,
	AVALANCHE = 3,
	// BONUS = 4,
	SCATTER_REPLACEMENT_AND_AVALANCHE = 4,
	// FREESPIN = 5,
}

// o    0 – Base Spin
// o    1 – Wild Expansion
// o    2 – Scatter Replacement
// o    3 – Win Avalanche
// o    4 – Scatter Replacement and Win Avalanche

export interface WildExpansion {
	multipliers: number[];
}

export interface MultiplierWilds {
	multipliers: number[];
	symbolIndexes: number[];
}

export interface Avalanche {
	symbolIndexes: number[];
}

export interface ScatterReplacements {
	scattersPositions: number[];
	symbolsPositions: number[];
}

export interface BonusGameSymbols {
	symbolPositions: number[];
}

export interface IRecodeError {
	attributes: {
		servercode: string;
		servertext: string;
		text: string;
		xmancode: string;
	};
}

export interface Win {
	amount: number;
	payline: number;
	payableID: number;
	winSymbols: number[];
	round: number;
	ways: number;
	multiplier: number;
	reason: string;
}

/**
 * state:
 * -1 = Invalid
 * 0 = Inactive
 * 1 = Active
 * 2 = Finished
 *
 * actionState:
 * -1 = Invalid
 * 0 = PickRequired = A chest needs to be opened
 * 1 = AcceptOrReject = A chest is currently open and needs to be claimed or dismissed
 * 2 = None = The bonus is over, no action required
 *
 * chests - the total number of chests the bonus game was initialised with
 * remaining - a comma separated list of available chest Ids
 * prize - final prize awarded to the player
 * win - final pay out awarded to the player
 * maxPrize - maximum prize on offer
 * maxWin - maximum pay out on offer
 */
export interface BonusGameData {
	actionState: number;
	bonusName: string;
	chests: number;
	maxPrize: number;
	maxWin: number;
	prize: number;
	remaining: number[];
	state: number;
	win: number;
	openedChests: Chest[];
}

export interface Chest {
	amount: number;
	id: number;
	prize: number;
	state: number;
}

export enum ReelsTypes {
	BASIC,
	BASIC_RESPIN,
	FREESPIN,
	FREESPIN_RESPIN,
	OTHER,
}

export class RecodeLogic extends Logic {
	public static readonly CENT_TO_A_DOLLAR: number = 0.01;
	public static readonly BET_WAYS_MULTIPLIER: number = 10; // Changed to 10 on 2019-06-28 to support lower min bet than 0.5$.

	protected _gameState: number;

	protected _loyalty: string;
	protected _userID: string;
	protected _transNumber: string;
	protected _totalWin: number;
	protected _type: string;
	protected _currency: string;

	protected _recodeBetValues: IRecodeBet[];
	protected _recodeAutoplayExtraData: IRecodeAutoPlayExtraConfig;

	protected _wins: Win[] = [];

	// Slot stuff
	protected _win: number;
	protected _transTotalWin: number;
	protected _stage: string;
	protected _nextStage: string;
	protected _multiplier: number;

	protected _reels: number[][];
	protected _reelTypes: ReelsTypes[];
	protected _reelsHeight: number[][];

	// for InGameProgress
	protected _roundTypes: RoundTypes[] = [];
	protected _currentRound: number = 0;
	protected _reelsIndexOffset: number = 0;
	protected _totalRounds: number = 0;

	protected _expandedWildsMultipliers: WildExpansion[] = [];
	protected _currentWildExpansion: number = 0;

	protected _baseWildsMultipliers: MultiplierWilds[] = [];
	protected _currentMultiplierWilds: number = 0;

	protected _scatterReplacements: ScatterReplacements[] = [];
	protected _currentScatterReplacement: number = 0;

	protected _waysRounds: number[] = [];

	protected _avalanches: Avalanche[] = [];
	protected _currentAvalanche: number = 0;

	protected _currentMultiplier: number = 1;

	protected _bonusGamesSymbols: BonusGameSymbols[] = [];
	protected _currentBonusGameSymbols: number = 0;
	protected _bonusGame: BonusGameData = null;

	protected _bonusSymbolsToChoose: number[] = [];

	protected _wildMeterDeltas: number[] = [];
	protected _wildMeter: number = 1;

	// Bet properties
	protected _chipSize: number;
	protected _numChips: number;
	protected _minChips: number;
	protected _maxChips: number;
	protected _slotBetMethod: number;
	protected _paylines: number;
	protected _betMultiplier: number = 0;
	protected _validChips: number[];

	// If there was a free games session store the lastRealWager.
	protected _lastRealWager: FreeGamesBetData = null;

	protected _latestRequest: string = '';

	// needed for one of the dirty hacks
	protected _alreadyExpandedWilds: number[];

	protected _freeGamesData: FreeGamesData[];

	protected _isInFreeRounds: boolean = false;

	protected _currentFreeGameProgress: FreeGamesInProgress;

	protected _hasExpiredFreeGames: boolean = false;

	protected _hasBonusGameFreeGamesRestore: boolean = false;

	protected _hasKilledFreeGamesRestore: boolean = false;

	protected _freeGamesBonusGameRestoreCallback: Function;

	protected _serverTime: string;

	protected _hasError: boolean = false;
	protected _isFatalError: boolean = false;

	protected _isRestoreAfterBonusGame: boolean = false;

	protected _hasClearedExpiredFreeGames: boolean = false;

	protected _restoredIntoBonusGame: boolean = false;
	protected _symbolsPerReel: number[];

	constructor(gameName: string, eventManager: IEventManagerWithAsync, communicator: RecodeIntegrator, device: Device) {
		super(gameName, eventManager, communicator, device);

		this.addDefaultValues();
		this.initAutoplayValues();
	}

	public killAllFreeGames(): void {
		this._freeGamesData = null;
		this._isInFreeRounds = false;
		this._currentFreeGameProgress = null;
		// this.validatePreviousBet();
	}

	public startFreeGames(): void {
		this._isInFreeRounds = true;
		this.setFreeGameBetValue();
	}

	public clearExpiredFreeGames(): void {
		this._currentFreeGameProgress = null;
		this._hasExpiredFreeGames = false;
		this._hasClearedExpiredFreeGames = true;
	}

	public setFreeGameBetValue(): void {
		if (this.getCurrentFreeGamesData()) {
			// Changed to corrected betlogic.
			this._betValue = this.calculateTotalBetValueFromChipsNumChipsMultiplier(
				this.getCurrentFreeGamesData().betData.chipSize,
				this.getCurrentFreeGamesData().betData.numChips,
			);
		} else {
			this._betValue = this.calculateTotalBetValueFromChipsNumChipsMultiplier(
				this.currentFreeGameProgress.wagerChipSize,
				this.currentFreeGameProgress.wagerNumChips,
			);
		}
	}

	public rejectFreeGames(cb: Function): void {
		(this._communicator as RecodeIntegrator).rejectFreeGames(
			this.getCurrentFreeGamesData().offerID,
			this.getCurrentFreeGamesData().instanceID,
			(data: any) => {
				cb && cb(null);
				this._eventManager.emit(RecodeLogicEvents.FREE_GAMES_REJECTED_RESPONSE);
			},
			null,
		);

		this.finishCurrentFreeGames();
	}

	public startNextRound(): void {
		if (this._currentRound < this._totalRounds - 1) {
			this._currentRound++;

			this.setResultsForCurrentRound();
		}
	}

	public makeNextScattersList(): void {
		const currentScatters: ScatterReplacements = this._scatterReplacements[this._currentScatterReplacement];
		this.getRecodeSpinResults().setScatters(currentScatters);
		this.getRecodeSpinResults().setRespinNewSymbols(
			this._reels[this._currentRound],
			currentScatters,
			this._reelsHeight[this._currentRound],
		);

		if (this._currentScatterReplacement < this._scatterReplacements.length - 1) {
			this._currentScatterReplacement++;
		}

		// Added for Medallion - every "tumble" should increase the multiplier.
		this._currentMultiplier++;
	}

	public makeNextAvalanchesList(): void {
		this.getRecodeSpinResults().setAvalancheSymbolsToRemove(
			this._avalanches[this._currentAvalanche].symbolIndexes,
			this._reels[this._currentRound - 1],
		);
		this.getRecodeSpinResults().setAvalancheNewSymbols(
			this._reels[this._currentRound - 1],
			this._reels[this._currentRound],
			this._avalanches[this._currentAvalanche].symbolIndexes,
			this._reelsHeight[this._currentRound - 1],
			this._reelsHeight[this._currentRound],
		);

		if (this._currentAvalanche < this._avalanches.length - 1) {
			this._currentAvalanche++;
		}
		this._currentMultiplier++;
	}

	public makeNextAvalancheScattersList(): void {
		// Scatter / Respin
		const currentScatters: ScatterReplacements = this._scatterReplacements[this._currentScatterReplacement];
		this.getRecodeSpinResults().setScatters(currentScatters);
		this.getRecodeSpinResults().setRespinNewSymbols(
			this._reels[this._currentRound],
			currentScatters,
			this._reelsHeight[this._currentRound],
		);

		if (this._currentScatterReplacement < this._scatterReplacements.length - 1) {
			this._currentScatterReplacement++;
		}
		// Avalanche
		this.getRecodeSpinResults().setAvalancheSymbolsToRemove(
			this._avalanches[this._currentAvalanche].symbolIndexes,
			this._reels[this._currentRound - 1],
		);

		this._avalanches[this._currentAvalanche].symbolIndexes = this._avalanches[this._currentAvalanche].symbolIndexes.concat([
			...currentScatters.scattersPositions,
			...currentScatters.symbolsPositions,
		]);

		this.getRecodeSpinResults().setAvalancheNewSymbols(
			this._reels[this._currentRound - 1],
			this._reels[this._currentRound],
			this._avalanches[this._currentAvalanche].symbolIndexes,
			this._reelsHeight[this._currentRound - 1],
			this._reelsHeight[this._currentRound],
		);

		if (this._currentAvalanche < this._avalanches.length - 1) {
			this._currentAvalanche++;
		}
		this._currentMultiplier++;
	}

	public makeNextBonusSymbolsList(): void {
		this.getRecodeSpinResults().setBonusSymbolsList(
			this._bonusGamesSymbols[this._currentBonusGameSymbols].symbolPositions,
			this._bonusGame.maxWin,
			this._bonusGame.openedChests.length > 0 ? this._bonusGame.openedChests[0].amount : 0,
		);

		if (this._currentBonusGameSymbols < this._bonusGamesSymbols.length - 1) {
			this._currentBonusGameSymbols++;
		}
	}

	public getPreviousRoundReels(): GameSymbol[][] {
		return this.getRecodeSpinResults().makeGameReelsFromRecodeReels(
			this._reels[this._currentRound - 1],
			this._reelsHeight[this._currentRound - 1],
		);
	}

	public hasScatterAvalanche(): boolean {
		return this.getCurrentRoundType() === RoundTypes.SCATTER_REPLACEMENT_AND_AVALANCHE;
	}

	public hasMultiplierWilds(): boolean {
		// TODO: This is shit, fix.
		if (this._currentRound === this._totalRounds - 1) {
			return (
				this._baseWildsMultipliers.length > 0 &&
				this._baseWildsMultipliers[this._currentRound] &&
				this._baseWildsMultipliers[this._currentRound].multipliers.length > 0
			);
		}

		return (
			this._baseWildsMultipliers.length > 0 &&
			this._baseWildsMultipliers[this._currentRound] &&
			this._baseWildsMultipliers[this._currentRound].multipliers.length > 0
		);
	}

	public clearAlreadyExpandedWilds(): void {
		this._alreadyExpandedWilds = [];
	}

	public addToAlreadyExpandedWilds(reelIndex: number): void {
		if (!this.isWildAlreadyExpanded(reelIndex)) {
			this._alreadyExpandedWilds.push(reelIndex);
		}
	}

	public removeFromAlreadyExpandedWilds(reelIndex: number): void {
		const index: number = this._alreadyExpandedWilds.indexOf(reelIndex);
		if (index > 0) {
			this._alreadyExpandedWilds.splice(index, 1);
		}
	}

	public makeNextAvalancheMultiplier(): void {
		this.getRecodeSpinResults().setCurrentAvalancheMultiplier(this._currentMultiplier);
	}

	public systemUpdateBalance(newBalance: number): void {
		this._balance = newBalance;
	}

	public getTransTotalWin(): number {
		return this._transTotalWin;
	}

	public isEnoughMoneyForSpin(): boolean {
		return this._balance >= this.getTotalBetCents() || this.isInFreeRounds;
	}

	public getTotalBetCents(): number {
		return this.betValue / RecodeLogic.CENT_TO_A_DOLLAR;
	}

	public getWildSymbol(): number {
		throw new Error('RecodeLogic::getWildSymbol function should be game specific');
	}

	public removeExpandedWild(reelIndex: number): void {
		this.getRecodeSpinResults().removeExpandedWild(reelIndex);
	}

	public finishRoundAfterBonusGame(): void {
		this._previousAction = this._nextAction;
		this._nextAction = Action.SPIN;
	}

	public hasFreeGames(): boolean {
		return this._freeGamesData && this._freeGamesData.length > 0;
	}

	public hasNextFreeGames(): boolean {
		if (this.currentFreeGameProgress) {
			const currentGameIndex: number = this.findFreeGamesIndex(
				this.currentFreeGameProgress.instanceID,
				this.currentFreeGameProgress.offerID,
			);
			if (currentGameIndex >= 0) {
				return this._freeGamesData.length > 1;
			}

			return this._freeGamesData.length > 0;
		}

		return this._freeGamesData.length > 1;
	}

	public getCurrentFreeGamesData(): FreeGamesData {
		return this._freeGamesData ? this._freeGamesData[0] : null;
	}

	public hasExpandingWilds(): boolean {
		if (this.getSpinResult().expandingWilds.length > 0) {
			for (const expWildReel of this.getSpinResult().expandingWilds) {
				if (!this.isWildAlreadyExpanded(expWildReel.reelIndex)) {
					return true;
				}
			}

			return false;
		} else {
			return false;
		}
	}

	public getRecodeSpinResults(): RecodeSpinResult {
		return this._spin as RecodeSpinResult;
	}

	public getCurrentRoundType(): number {
		return this._roundTypes[this._currentRound];
	}

	public getNextRoundType(): number {
		if (this._currentRound < this._roundTypes.length - 1) {
			return this._roundTypes[this._currentRound + 1];
		}

		return -1;
	}

	public getMultiplierForCurrentRound(): number {
		return this.getMultiplierForRound(this._currentRound);
	}

	public getCurrentFreeSpinsMultiplier(): number {
		return this._freeSpinsPreviousMultiplier;
	}

	public isGameInProgress(): boolean {
		return this._latestRequest !== 'init';
	}

	public postponeFreeGames(): void {
		this._freeGamesData.splice(0, 1);

		if (!this.getCurrentFreeGamesData()) {
			this._freeGamesData = null;
			this._isInFreeRounds = false;
			this._currentFreeGameProgress = null;
		} else {
			if (!this._currentFreeGameProgress) {
				this._currentFreeGameProgress = new FreeGamesInProgress(null);
			}

			this._currentFreeGameProgress.gamesPlayed =
				this.getCurrentFreeGamesData().gamesAwarded - this.getCurrentFreeGamesData().gamesRemaining;
			this._currentFreeGameProgress.offerID = this.getCurrentFreeGamesData().offerID;
			this._currentFreeGameProgress.instanceID = this.getCurrentFreeGamesData().instanceID;
			this._currentFreeGameProgress.gamesRemaining = this.getCurrentFreeGamesData().gamesRemaining;
			this._currentFreeGameProgress.sumWinnings = this.getCurrentFreeGamesData().sumWinnings;
		}
	}

	public makeCurrenFreeGamesDataProgress(): void {
		if (!this._currentFreeGameProgress) {
			this._currentFreeGameProgress = this.createNewFreeGameInProgress(null);
		}
		this._currentFreeGameProgress.gamesPlayed =
			this.getCurrentFreeGamesData().gamesAwarded - this.getCurrentFreeGamesData().gamesRemaining;
		this._currentFreeGameProgress.offerID = this.getCurrentFreeGamesData().offerID;
		this._currentFreeGameProgress.instanceID = this.getCurrentFreeGamesData().instanceID;
		this._currentFreeGameProgress.gamesRemaining = this.getCurrentFreeGamesData().gamesRemaining;
		this._currentFreeGameProgress.sumWinnings = this.getCurrentFreeGamesData().sumWinnings;
	}

	public finishCurrentFreeGames(): void {
		if (this.currentFreeGameProgress) {
			const currentGameIndex: number = this.findFreeGamesIndex(
				this.currentFreeGameProgress.instanceID,
				this.currentFreeGameProgress.offerID,
			);
			if (currentGameIndex >= 0) {
				this._freeGamesData.splice(currentGameIndex, 1);
			}
		}

		this.setupFreeGameAfterFinishingCurrent();
	}

	public init(cb: Function): void {
		this.parsePlayerSavedData();

		this._latestRequest = 'init';
		this._communicator.init(this._gameName, (data: any) => {
			// Check if there is an initialization error before anything else.
			if (data.Id.attributes.verb === 'Error') {
				if (!__PRODUCTION__) {
					console.log(data);
				}

				this.parseError(data);
			}

			// Stop it here and don't continue. All errors are fatal in Recode.
			if (this._hasError && this._isFatalError) {
				return;
			}

			this._freeGamesData = [];
			this.parseFreeGames(data);

			if (this._freeGamesData.length > 0 && data.Response.FreeGames.CurrentGame) {
				this._currentFreeGameProgress = this.createNewFreeGameInProgress(data);
				if (this._currentFreeGameProgress.gamesRemaining === 0) {
					this._hasRestore = false;
				}
			} else {
				this.parseExpiredGames(data);
				if (this._currentFreeGameProgress && this._currentFreeGameProgress.gamesRemaining === 0) {
					this._hasRestore = false;
				}
			}

			if (!this.isCurrentGameInFreeGamesList()) {
				if (!this.shouldShowCurrentFreeSpinsFinishedScreen() && !this._hasExpiredFreeGames) {
					this._currentFreeGameProgress = null;
				}
			}

			this._spin = this.createSpinResult();
			this.parseInit(data);

			if (this._hasRestore) {
				this.restoreRecodeBet();
			}

			if (this.mustRestoreAfterBonusGame()) {
				// this._isRestoreAfterBonusGame = true;
				// this._hasRestore = true;
				// this.makeNextBonusSymbolsList();
				// this._roundTypes.push(RoundTypes.BONUS);
				// if (this._roundTypes.length === 2) {
				// 	this.setNextAction();
				// }
			} else {
				this._isRestoreAfterBonusGame = false;
			}

			if (
				this._gameState === 2 &&
				this.hasFreeGames() &&
				this._currentFreeGameProgress &&
				this._currentFreeGameProgress.gamesRemaining > 0
			) {
				this._hasBonusGameFreeGamesRestore = true;
				this._hasRestore = false;
			}

			if (this._gameState === 2 && this._bonusGame.openedChests.length === 0) {
				this._spin = this.createSpinResult();
				this.parseSpin(data);
				cb && cb(data);
				this._eventManager.emit(LogicEvents.INIT_RESPONSE);
			} else {
				cb && cb(data);
				this._eventManager.emit(LogicEvents.INIT_RESPONSE);
			}
		});
	}

	public saveData(): void {
		// TODO: Get the last autoplay value.
		const autoplayData = {autoplayValue: this.autoplayDefault};

		// TODO: Verify if lastbet is correct.
		const lastBet = this.betValue;
		const lastBetWasMaxBet = this.isMaxBet(this.betValue);
		const lastAutoplayAmount = this.lastAutoplayAmount;
		const soundActive = this.soundEffectsActive;
		const musicActive = this.musicActive;
		const soundAllActive = this.soundAllActive;
		const skipIntro = this.skipIntro;
		const spacebarSpinActive = this.spacebarSpinActive;
		const dragReelToSpinActive = this.dragReelToSpinActive;
		const quickStopActive = this.quickStopActive;
		const fastPlayActive = this.fastPlayActive;

		const data = {
			playerData: {
				autoplayData,
				lastBet,
				lastBetWasMaxBet,
				lastAutoplayAmount,
				soundActive,
				musicActive,
				soundAllActive,
				skipIntro,
				spacebarSpinActive,
				dragReelToSpinActive,
				quickStopActive,
				fastPlayActive,
			},
		};

		this._storage.setItem(`FNG_${this._gameName}`, JSON.stringify(data));
	}

	/**
	 * returns true if coin size changed dialog was shown
	 * @param callback
	 */
	public validatePreviousBet(callback: Function = () => {}): boolean {
		if (this.isInFreeRounds) {
			return false;
		}

		if (
			this.lastRealWager &&
			typeof this.lastRealWager.numChips !== 'undefined' &&
			typeof this.lastRealWager.chipSize !== 'undefined'
		) {
			this._numChips = this.lastRealWager.numChips;
			this._chipSize = this.lastRealWager.chipSize;
			// this._betMultiplier = this.lastRealWager.activePaylines;
		}

		const lastBet = this.getRecodeCurrentBet();

		const isBetValueNotValid = !this.isValidBetValue(lastBet);

		return this.setValidBet(lastBet, isBetValueNotValid, callback);
	}

	public recodeFreeGamesBonusGameRestore(): void {
		this._hasRestore = true;
		this._freeGamesBonusGameRestoreCallback();
		this._hasBonusGameFreeGamesRestore = false;
	}

	public recodeFreeGamesBonusGameRestoreRejected(): void {
		this._hasBonusGameFreeGamesRestore = false;
		this._gameState = 0;
		this._previousAction = this._nextAction;
		this._nextAction = Action.SPIN;
	}

	public recodeExpireGame(instanceID: string, offerID: string): void {
		if (
			this.currentFreeGameProgress &&
			this.currentFreeGameProgress.instanceID === instanceID &&
			this.currentFreeGameProgress.offerID === offerID
		) {
			this._currentFreeGameProgress = null;
		}
		const freeGameIndex: number = this.findFreeGamesIndex(instanceID, offerID);
		if (freeGameIndex >= 0) {
			this._freeGamesData.splice(freeGameIndex, 1);
		}

		this.setupFreeGameAfterFinishingCurrent();
	}

	public spin(cb: Function): void {
		this._latestRequest = 'spin';
		this.preSpinBalance = this.currentBalance;
		this.saveData();

		if (this.freeSpinsLeft > 0) {
			this.freespin(cb);

			return;
		}

		let currentFreeGames: FreeGamesData;

		if (this.isInFreeRounds) {
			currentFreeGames = this.currentFreeGameProgress
				? this.findFreeGame(this.currentFreeGameProgress.instanceID, this.currentFreeGameProgress.offerID)
				: this.getCurrentFreeGamesData();
		}

		this._communicator.spin(
			this._gameName,
			this._sessionID,
			this.getRecodeBet(this.betValue),
			(data: any) => {
				this.getRecodeSpinResults().cleanup();
				this.setSpin(this.createSpinResult());
				this.parseSpin(data);

				if (this._isInFreeRounds) {
					this._currentFreeGameProgress = this.createNewFreeGameInProgress(data);
				} else {
					this._currentFreeGameProgress = null;
				}

				if (this._hasError) {
					return;
				}

				cb && cb(data);
				this._eventManager.emit(LogicEvents.SPIN_RESPONSE);
			},
			currentFreeGames,
		);
	}

	public freespin(cb: Function): void {
		this._latestRequest = 'freespin';
		this.saveData();

		this._communicator.freespin((data: any) => {
			this.getRecodeSpinResults().cleanup();
			this.setSpin(this.createSpinResult());
			this.parseSpin(data);

			if (this._hasError) {
				return;
			}

			cb && cb(data);
			this._eventManager.emit(LogicEvents.SPIN_RESPONSE);
			// this._eventManager.emit(LogicEvents.FREE_SPIN_RESPONSE); // Setup Listeners
		});
	}

	public respin(cb: Function): void {
		// this.makeNextScattersList();

		setTimeout(() => {
			cb && cb(null);
			this._eventManager.emit(LogicEvents.RESPIN_RESPONSE);
		});
	}

	public pick(cb: Function, pickId: number = null): void {
		this._latestRequest = 'pick';
		if (!pickId) {
			pickId = this._bonusGame.openedChests.length > 0 ? this._bonusGame.openedChests[this._bonusGame.openedChests.length - 1].id : 0;
		}

		this._communicator.pick(
			pickId,
			(data: any) => {
				this.setSpin(this.createSpinResult());
				this.parseSpin(data);
				if (this._hasError) {
					return;
				}
				this.tryToParseFreeGameInProgress(data);
				cb && cb(null);
				this._eventManager.emit(LogicEvents.PICK_RESPONSE);
			},
			null,
		);
	}

	public skippick(cb: Function): void {
		this._latestRequest = 'skippick';
		const latestPickID: number =
			this._bonusGame.openedChests.length > 0 ? this._bonusGame.openedChests[this._bonusGame.openedChests.length - 1].id : 0;

		if (this._bonusGame.remaining.length > 1) {
			this._communicator.skippick(
				latestPickID,
				(data: any) => {
					cb && cb(null);
					this._eventManager.emit(LogicEvents.SKIPPICK_RESPONSE);
				},
				null,
			);
		} else {
			// Test if final chest works when we send a pick event.
			// Last chest is auto-picked on server once we send the skippick for second-to-last chest. (as described in math doc) So sending anything other will result in Error as a response.
			this._communicator.skippick(
				latestPickID,
				(data: any) => {
					this.setSpin(this.createSpinResult());
					this.parseSpin(data);
					if (this._hasError) {
						return;
					}
					this.tryToParseFreeGameInProgress(data);
					cb && cb(null);
					this._eventManager.emit(LogicEvents.SKIPPICK_RESPONSE);
				},
				null,
			);
		}
	}

	protected tryToParseFreeGameInProgress(data: any): void {
		if (this._isInFreeRounds) {
			this._currentFreeGameProgress = this.createNewFreeGameInProgress(data);
		} else {
			this._currentFreeGameProgress = null;
		}
	}

	protected replaceElementInToChooseList(chooseIndex: number, remainingList: number[]): void {
		if (remainingList && remainingList.length >= 3) {
			this._bonusSymbolsToChoose[chooseIndex] = remainingList[2];
		} else {
			this._bonusSymbolsToChoose[chooseIndex] = null;
		}
	}

	protected setupFreeGameAfterFinishingCurrent(): void {
		if (!this.getCurrentFreeGamesData()) {
			this._freeGamesData = null;
			this._isInFreeRounds = false;
			this._currentFreeGameProgress = null;
		} else {
			if (!this._currentFreeGameProgress) {
				this._currentFreeGameProgress = new FreeGamesInProgress(null);
			}

			this._currentFreeGameProgress.gamesPlayed =
				this.getCurrentFreeGamesData().gamesAwarded - this.getCurrentFreeGamesData().gamesRemaining;
			this._currentFreeGameProgress.offerID = this.getCurrentFreeGamesData().offerID;
			this._currentFreeGameProgress.instanceID = this.getCurrentFreeGamesData().instanceID;
			this._currentFreeGameProgress.gamesRemaining = this.getCurrentFreeGamesData().gamesRemaining;
			this._currentFreeGameProgress.sumWinnings = this.getCurrentFreeGamesData().sumWinnings;
		}
	}

	protected findFreeGamesIndex(instanceID: string, offerID: string): number {
		if (this._freeGamesData) {
			let index: number = 0;
			for (const fg of this._freeGamesData) {
				if (fg.instanceID === instanceID && fg.offerID === offerID) {
					return index;
				}
				index++;
			}
		}

		return -1;
	}

	protected findFreeGame(instanceID: string, offerID: string): FreeGamesData {
		let index: number = 0;
		for (const fg of this._freeGamesData) {
			if (fg.instanceID === instanceID && fg.offerID === offerID) {
				return fg;
			}
			index++;
		}

		return null;
	}

	protected restoreRecodeBet(): void {
		const lastBet = this.getRecodeCurrentBet();

		// const test = !this.hasRestore && !this.isValidBetValue(lastBet);
		// this.setValidBet(lastBet, test);
		this.setValidBet(lastBet, false);
	}

	protected createSpinResult(): SpinResult {
		return new RecodeSpinResult();
	}

	protected createStorage(): void {
		this._storage = new ReCodeStorage((window as any).V.API.Storage);
	}

	protected createCurrencyFormatter(): void {
		this._currencyFormatter = new RecodeCurrencyFormatter();
	}

	protected stringListToInt(stringList: string[]): number[] {
		const resultList = [];
		for (const value of stringList) {
			if (value.toLocaleLowerCase() === 'none') {
				resultList.push(0);
			} else {
				resultList.push(parseInt(value, 10));
			}
		}

		return resultList;
	}

	protected stringListToFloat(stringList: string[]): number[] {
		const resultList = [];
		for (const value of stringList) {
			if (value.toLocaleLowerCase() === 'none') {
				resultList.push(0);
			} else {
				resultList.push(parseFloat(value));
			}
		}

		return resultList;
	}

	protected initAutoplayValues(): void {
		const autoplayValues: IRecodeAutoPlayConfig = (this.communicator as RecodeIntegrator).getAutoplayData();

		const enabled = autoplayValues.enabled;

		const winLimitsAmounts = this.stringListToInt(autoplayValues.winLimitAmounts);
		const lossLimitsAmounts = this.stringListToInt(autoplayValues.lossLimitAmounts);

		///////////////////////////////////////////////////////////////////////
		// As per AT1 in Medallion. WIN and LOSS limits should be in "dollars".
		winLimitsAmounts.forEach((item, index, array) => {
			array[index] = item * RecodeLogic.CENT_TO_A_DOLLAR;
		});

		lossLimitsAmounts.forEach((item, index, array) => {
			array[index] = item * RecodeLogic.CENT_TO_A_DOLLAR;
		});

		const winLimitDefault = parseInt(autoplayValues.winLimitDefault, 10) * RecodeLogic.CENT_TO_A_DOLLAR;
		const lossLimitDefault = parseInt(autoplayValues.lossLimitDefault, 10) * RecodeLogic.CENT_TO_A_DOLLAR;
		///////////////////////////////////////////////////////////////////////

		const winLimitEnabled = autoplayValues.winLimitEnabled;
		const lossLimitEnabled = autoplayValues.lossLimitEnabled;

		const spinDefault = autoplayValues.spinCount[0];
		const spinCount = autoplayValues.spinCount;

		const untilStop = autoplayValues.untilStop;
		const maxSpinsUntilStop = parseInt(autoplayValues.maxSpinsUntilStop, 10);

		const alwaysDisplayCustomScreen = autoplayValues.alwaysDisplayCustomScreen;
		const hasCustom = autoplayValues.hasCustom;
		const useWagerLimitApi = autoplayValues.useWagerLimitApi;

		this._autoplayData = {
			enabled,
			spinCount,
			spinDefault,
			untilStop,
			maxSpinsUntilStop,
			winLimitEnabled,
			winLimitsAmounts,
			winLimitDefault,
			lossLimitEnabled,
			lossLimitDefault,
			lossLimitsAmounts,
		};

		this._recodeAutoplayExtraData = {
			hasCustom,
			alwaysDisplayCustomScreen,
			useWagerLimitApi,
		};
	}

	protected getRecodeBet(bet: number): IRecodeBet {
		const betItem = this._recodeBetValues.find((item) => item.totalValue === bet);

		return betItem;
	}

	protected addDefaultValues(): void {
		this._balance = 1000;
		this._betValue = 1;
		this._betLevel = 1;
		this._win = 0;

		this.autoplayDefault = 25;
		this.autoplayValues = [10, 25, 50, 100, 200, 500];
	}

	protected createCommunicator(communicator: Communicator): void {
		this._communicator = communicator;
		// (window as any).fake = this._communicator;

		if (this._queryStringHandler) {
			this.setCommunicatorValues();
		}
	}

	protected createNewFreeGameInProgress(data: any): FreeGamesInProgress {
		const freeGames = new FreeGamesInProgress(data);
		this.lastRealWager = freeGames.lastRealWager ? freeGames.lastRealWager : this.lastRealWager;

		return freeGames;
	}

	protected setValidBet(bet: number, isBetValueNotValid: boolean, callback: Function = () => {}): boolean {
		if (isBetValueNotValid) {
			// If the last selected bet value is invalid. Pick the closest available bet value instead. Unless last bet was a MAX bet. Then go with that.
			// Just go to closest bet. Max bet caused some weird issues in some cases. (like 0.5$ being max and it restored to 1000$).
			// (window as any).V.API.Dialog.showCoinSizeChangedDialog(callback);

			(this._communicator as RecodeIntegrator).api_showCoinSizeChangedDialog(callback);
			// bet = this._lastBetWasMaxBet ? this.getMaxValidBet() : this.getClosestBet(bet);
			bet = this.getClosestBet(bet);
		}

		this._betValue = bet;
		this._lastBet = this._betValue;
		this.saveData();

		if (!isBetValueNotValid && callback) {
			callback();
		}

		return isBetValueNotValid;
	}

	protected mustRestoreAfterBonusGame(): boolean {
		return (
			this.hasRestore &&
			this.gameState === 0 &&
			this.recodeBonusGame &&
			this.recodeBonusGame.openedChests &&
			this.recodeBonusGame.openedChests.length > 0
		);
	}

	protected shouldShowCurrentFreeSpinsFinishedScreen(): boolean {
		return !this.isCurrentGameInFreeGamesList() && this._currentFreeGameProgress && this._currentFreeGameProgress.gamesRemaining === 0;
	}

	protected isCurrentGameInFreeGamesList(): boolean {
		if (this._currentFreeGameProgress) {
			for (const currentFG of this._freeGamesData) {
				if (
					currentFG.instanceID === this._currentFreeGameProgress.instanceID &&
					currentFG.offerID === this._currentFreeGameProgress.offerID
				) {
					return true;
				}
			}
		}

		return false;
	}

	protected parseFreeGames(data: any): void {
		this._serverTime = '';
		if (data.Response && data.Response.FreeGames && data.Response.FreeGames.Games && data.Response.FreeGames.Games.FreeGame) {
			if (!data.Response.FreeGames.Games.FreeGame.length) {
				data.Response.FreeGames.Games.FreeGame = [data.Response.FreeGames.Games.FreeGame];
			}

			if (data.Response.FreeGames.Games.attributes.serverTime) {
				this._serverTime = data.Response.FreeGames.Games.attributes.serverTime;
			}

			for (const freeGameData of data.Response.FreeGames.Games.FreeGame) {
				const freeGame: FreeGamesData = new FreeGamesData(freeGameData);
				if (this._serverTime) {
					const serverTimer: number = GameUtils.unixDateToSeconds(this.serverTime);
					const endTime: number = GameUtils.unixDateToSeconds(freeGame.endDate);

					const timeBeforeExpiration: number = endTime - serverTimer;
					if (timeBeforeExpiration > 0) {
						this._freeGamesData.push(new FreeGamesData(freeGameData));
					}
				} else {
					this._freeGamesData.push(new FreeGamesData(freeGameData));
				}
			}
		}
	}

	protected parseExpiredGames(data: any): void {
		this._currentFreeGameProgress = null;

		if (data.Response && data.Response.FreeGames && data.Response.FreeGames.Games && !data.Response.FreeGames.Games.FreeGame) {
			this._currentFreeGameProgress = this.createNewFreeGameInProgress(data);
			this._hasExpiredFreeGames = true;
		}
	}

	protected cleanup(): void {}

	protected parseSpin(data: any): void {
		////////////////////////////////////////////////////////////////////
		// Custom Spin override
		////////////////////////////////////////////////////////////////////
		if (!__PRODUCTION__) {
			if ((document as any).spinOverride) {
				data = (document as any).spinOverride;
			}

			if (!(document as any).previousSpins || (document as any).previousSpins.length < 1) {
				(document as any).previousSpins = [];
			}

			(document as any).previousSpins.push(data);
			(document as any).lastSpinData = data;

			console.log('spin');
			console.log(data);
		}

		////////////////////////////////////////////////////////////////////
		// End of override
		////////////////////////////////////////////////////////////////////

		this.cleanup();
		this._bonusGame = null;
		this.parseID(data.Id.attributes);
		this.parseResponse(data);

		if (this._hasError) {
			return;
		}

		this.setResultsForCurrentRound();
	}

	protected parseInit(data: any): void {
		////////////////////////////////////////////////////////////////////
		// Custom Spin override
		////////////////////////////////////////////////////////////////////
		if (!__PRODUCTION__) {
			console.log('Init:', data);
			if ((document as any).spinOverride) {
				data = (document as any).spinOverride;
			}

			if (!(document as any).previousSpins || (document as any).previousSpins.length < 1) {
				(document as any).previousSpins = [];
			}

			(document as any).previousSpins.push(data);
			(document as any).lastSpinData = data;
		}
		////////////////////////////////////////////////////////////////////
		// End of override
		////////////////////////////////////////////////////////////////////

		this._reelsIndexOffset = 0;
		this.clearAlreadyExpandedWilds();
		this.parseID(data.Id.attributes);
		this.parseResponse(data);

		this.restoreRecodeBet();
		this.initAutoplayDefaultValues();
		this.setupPaytable();

		this.setInitResults();

		if (this._hasRestore) {
			this.setResultsForCurrentRound();
		}
	}

	protected changeCurrentReelsetToFirstOfFreeSpins(): void {
		for (let i = 0; i < this._reelTypes.length; i++) {
			if (this._reelTypes[i] === ReelsTypes.FREESPIN) {
				this._reelsIndexOffset = i;

				return;
			}
		}
	}

	protected setupPaytable(): void {}

	protected parseID(data: any): void {
		this._sessionID = data.sessionid;
	}

	protected parsePaytable(data: any): void {
		this._paytable = {};

		for (const symbol in data) {
			if (data.hasOwnProperty(symbol)) {
				const element = data[symbol];
				const symbolValues: ISymbolValue = {id: element.id, values: {}};

				for (const count in element.values) {
					if (element.values.hasOwnProperty(count)) {
						const winAmount = element.values[count];
						symbolValues.values[count] = winAmount;
					}
				}
				this._paytable[`${symbol.toString()}`] = symbolValues;
			}
		}
	}

	protected parseResponse(data: any): void {
		this._hasRestore = false;
		this._isRestoreAfterBonusGame = false;
		this._wildMeter = 0;

		const dataResponse = data.Response;

		if (data.Id.attributes.verb === 'Error') {
			this.parseError(data);
		}
		if (!dataResponse.Error) {
			this.parseSlot(dataResponse.Slot);
		}

		if (dataResponse.Tokens) {
			this.parseTokens(dataResponse.Tokens);
		}

		this.prepareInGameProgressValues();
		if (dataResponse.InGameProgress) {
			this.parseInGameProgress(dataResponse.InGameProgress);

			if (this.hasRestoreCondition()) {
				this._hasRestore = true;

				this.prepareFreeSpinsRestoreData(data);
			}

			if (this.isFreeSpinsStarted && this._latestRequest === 'init' && this.freeSpinsLeft < this.freespinsTotalAwarded) {
				this._freeSpinsPreviousMultiplier = this._freeSpinsMultiplier - this.getNumberOfMultiplierIncreasingRounds();
			}
		} else if (this.hasRestoreCondition()) {
			this._hasRestore = true;

			this.prepareFreeSpinsRestoreData(data);
		} else {
			// TODO: Remove if Recode provides symbol sizes.
			this.tempInitSymbolSizes();
		}

		if (!dataResponse.Error) {
			this.parseGameState(dataResponse.Game);
			this.parsePlayerData(dataResponse.Player);
			this.parseBet(dataResponse.Bet);
			this._hasError = false;
		}

		if (this._latestRequest === 'init') {
			this.setValidBetValues();
			this.parseDefaultBet();
		}

		if (dataResponse.BonusGames && dataResponse.BonusGames.Bonus) {
			this.parseBonus(dataResponse.BonusGames.Bonus);
		}

		// adding bonus game to game round types
		if (this._gameState === 2) {
			// this._roundTypes.push(RoundTypes.BONUS);
		}

		if (this._gameState === 3) {
			// this._roundTypes.push(RoundTypes.FREESPIN);
		}
	}

	protected prepareFreeSpinsRestoreData(data: any): void {
		if (this._freespinsTotalAwarded > 0 && (this._freeSpinsLeft || this.hasRestore)) {
			this._isFreeSpinsStarted = true;
			this._freeSpinsMultiplier = Number.parseInt(data.Response.Slot.attributes.multiplier, 10);
		}

		if (this.shouldRemovePreviousRoundWin()) {
			this._wins = [];
			this._roundTypes = [0];
			this._avalanches = [];
			this._expandedWildsMultipliers = [];
		}
	}

	protected hasFreeSpinsRestoreCondition(): boolean {
		return this._freespinsTotalAwarded > 0 && this._freeSpinsLeft > 0;
	}

	protected hasRestoreCondition(): boolean {
		return (
			!this._hasBonusGameFreeGamesRestore &&
			this._latestRequest === 'init' &&
			(this._roundTypes.length > 1 ||
				(this._gameState !== 0 && this._gameState !== undefined) ||
				this._win > 0 ||
				this.hasFreeSpinsRestoreCondition())
		);
	}

	protected shouldRemovePreviousRoundWin(): boolean {
		return this.isFreeSpinsStarted && this._freespinsTotalAwarded === this._freeSpinsLeft;
	}

	protected parseError(data: any): void {
		const errordata: IRecodeError = data.Response.Error;

		if (errordata.attributes.servercode === '512') {
			this._isFatalError = false;
			this._communicator.api_showErrorMessage(errordata);
		} else {
			this._isFatalError = true;
			// Added for handling unknown errors.
			let includeErrorText: any = false;

			if (!__PRODUCTION__) {
				includeErrorText = errordata.attributes.text;
			}

			const errorDetails: any = (window as any).V.API.Core.getErrorDetails(data, includeErrorText);
			(window as any).V.API.Core.handleFatalError(errorDetails);
		}

		this._hasError = true;
	}

	protected parseBonus(data: any): void {
		this._bonusGame = {
			actionState: Number.parseInt(data.attributes.actionState, 10),
			bonusName: data.attributes.bonusName,
			chests: Number.parseInt(data.attributes.chests, 10),
			maxPrize: Number.parseInt(data.attributes.maxPrize, 10),
			maxWin: Number.parseInt(data.attributes.maxWin, 10),
			prize: Number.parseInt(data.attributes.prize, 10),
			remaining: [],
			state: Number.parseInt(data.attributes.state, 10),
			win: this.parseDollar(data.attributes.win),
			openedChests: [],
		};
		if (data.attributes.remaining && data.attributes.remaining.length > 0) {
			const remaining: string[] = data.attributes.remaining.split(',');
			remaining.forEach((value) => {
				this._bonusGame.remaining.push(Number.parseInt(value, 10));
			});
		}

		if (data.Chests && data.Chests.Chest) {
			this.parseChests(data.Chests.Chest);
		}
	}

	protected parseChests(data: any): void {
		if (!data.length) {
			data = [data];
		}
		for (const chest of data) {
			const index: number = Number.parseInt(chest.attributes.id, 10);
			const amount: number = Number.parseInt(chest.attributes.amount, 10);
			const prize: number = Number.parseInt(chest.attributes.prize, 10);
			const state: number = Number.parseInt(chest.attributes.amount, 10);
			this._bonusGame.openedChests.push({
				id: index,
				amount,
				prize,
				state,
			});
		}
	}

	protected parseGameState(data: any): void {
		this._gameState = Number.parseInt(data.attributes.state, 10);
	}

	protected parsePlayerData(data: any): void {
		// this._balance = this.parseDollar(data.attributes.balance);
		this._balance = parseInt(data.attributes.balance, 10); // Should be received in "cents"
		this._loyalty = data.attributes.loyalty;
		this._userID = data.attributes.userID;
		this._transNumber = data.attributes.transNumber;
		this._totalWin = this.parseDollar(data.attributes.totalWin);
		this._type = data.attributes.type;
		this._currency = data.attributes.currency;
	}

	protected parseDefaultBet(): void {
		this._defaultBet = this.isValidBetValue(0.5) ? 0.5 : this.getLowerBet(0.5);
	}

	protected parseBet(data: any): void {
		// chipSize: int
		// The chip / coin size for the current bet.

		// numChips: int
		// The number of chips / coins for the current bet.

		// activePaylines: string
		//    This is a list of the current active pay lines for the bet which will contain the player’s current selection.

		// maxChips: int
		// This is the maximum value that service will accept when specifying numChips in any incoming packet.

		// minChips: int
		// This is the minimum value that service will accept when specifying numChips in any incoming packet.

		// slotBetMethod: int
		// This value can be ignored by the client.

		// paylines: int
		// The maximum number of pay lines.

		// multiplier: int
		// Used as the ways multiplier or an alternative cost value to the number of pay lines.
		// This value will only be in the Refresh data if your bet profile requires it.

		// validChips: string
		// This is a list of comma separated chip sizes.
		// These are the values that the service will accept when specifying chipSize in any incoming packet.

		this._chipSize = Number.parseInt(data.attributes.chipSize, 10);
		this._numChips = Number.parseInt(data.attributes.numChips, 10);
		this._maxChips = Number.parseInt(data.attributes.maxChips, 10);
		this._minChips = Number.parseInt(data.attributes.minChips, 10);
		this._slotBetMethod = Number.parseInt(data.attributes.slotBetMethod, 10); // Unused
		this._paylines = Number.parseInt(data.attributes.paylines, 10);
		if (this._betMultiplier > 0 && !data.attributes.multiplier) {
			// do nothing
		} else {
			this._betMultiplier = data.attributes.multiplier
				? Number.parseInt(data.attributes.multiplier, 10)
				: RecodeLogic.BET_WAYS_MULTIPLIER;
		}

		if (data.attributes.validChips) {
			const chips: string[] = data.attributes.validChips.split(',');
			this._validChips = [];
			chips.forEach((value) => {
				this._validChips.push(Number.parseInt(value, 10));
			});
		}
	}

	protected setValidBetValues(): void {
		this._betValues = [];
		this._recodeBetValues = [];

		for (let chip = this._minChips; chip <= this._maxChips; chip++) {
			for (const chipItem in this._validChips) {
				if (this._validChips.hasOwnProperty(chipItem)) {
					const chipSize = this._validChips[chipItem];
					const numChips = chip;
					const totalValue = GameUtils.truncateValue(
						this.calculateTotalBetValueFromChipsNumChipsMultiplier(chipSize, numChips),
						2,
					);

					const betItem: IRecodeBet = {
						chipSize,
						numChips,
						totalValue,
					};

					if (this._betValues.indexOf(totalValue) === -1) {
						this._betValues.push(totalValue);
						this._recodeBetValues.push(betItem);
					}
				}
			}
		}

		this._betValues.sort((a, b) => {
			return a - b;
		});
	}

	protected calculateTotalBetValueFromChipsNumChipsMultiplier(chipSize: number, numChips: number): number {
		return chipSize * numChips * this._betMultiplier * RecodeLogic.CENT_TO_A_DOLLAR;
	}

	protected getRecodeCurrentBet(): number {
		return GameUtils.truncateValue(this.calculateTotalBetValueFromChipsNumChipsMultiplier(this._chipSize, this._numChips), 2);
	}

	public get numChips(): number {
		return this._numChips;
	}

	public get chipSize(): number {
		return this._chipSize;
	}

	public get validChips(): number[] {
		return this._validChips;
	}

	protected isValidChipValue(chip: number): boolean {
		return this._validChips.indexOf(chip) > -1;
	}

	protected isValidBetValue(betValue: number): boolean {
		return this._betValues.indexOf(betValue) > -1;
	}

	protected getMinimumValidBet(): number {
		return this._betValues[0];
	}

	protected getMaxValidBet(): number {
		return this._betValues[this._betValues.length - 1];
	}

	protected parseDollar(value: string): number {
		return Number.parseInt(value, 10) * RecodeLogic.CENT_TO_A_DOLLAR;
	}

	protected parseSlot(data: any): void {
		this._win = this.parseDollar(data.attributes.win);
		this._transTotalWin = this.parseDollar(data.attributes.transTotalWin);
		this._stage = data.attributes.stage;
		this._nextStage = data.attributes.nextStage;
		this._multiplier = Number.parseInt(data.attributes.multiplier, 10);

		this._freespinsTotalAwarded = Number.parseInt(data.attributes.freeSpinTotalAwarded, 10);
		this._freeSpinsTotalWin = this.parseDollar(data.attributes.freeSpinTotalWin);
		this._freeSpinsLeft = Number.parseInt(data.attributes.freeSpins, 10);

		if (this._latestRequest === 'freespin') {
			this._freeSpinsPreviousMultiplier = this._freeSpinsMultiplier;
		} else {
			this._freeSpinsPreviousMultiplier = 1;
		}
		if (this.isFreeSpinsStarted) {
			this._freeSpinsMultiplier = Number.parseInt(data.attributes.multiplier, 10);
		} else {
			this._freeSpinsMultiplier = 1;
		}

		if (!__PRODUCTION__) {
			console.warn(data.attributes);
		}

		this._scatterReplacements = [];
		this._currentScatterReplacement = 0;

		this._bonusGamesSymbols = [];
		this._currentBonusGameSymbols = 0;

		this._wins = [];

		this.parseResultSets(data);
		if (data.Wins) {
			this.parseWins(data.Wins);
		}
	}

	protected getNumberOfMultiplierIncreasingRounds(): number {
		let rounds: number = 0;
		for (const val of this._roundTypes) {
			if (
				val === RoundTypes.SCATTER_REPLACEMENT_AND_AVALANCHE ||
				val === RoundTypes.AVALANCHE ||
				val === RoundTypes.SCATTER_REPLACEMENT
			) {
				rounds++;
			}
		}

		return rounds;
	}

	protected parseResultSets(data: any): void {
		this._reels = [];

		const stage: string = data.attributes.stage;
		const results: any[] = data.ResultSets.Result;
		let reelsIndex: number = 0;

		for (const res of results) {
			const name: string = res.attributes.name;

			if (name.includes(stage)) {
				if (!res.Reels) {
					continue;
				}

				if (!res.Reels.length) {
					res.Reels = [res.Reels];
				}

				for (const reel of res.Reels) {
					this._reels[reelsIndex] = [];
					const reelSymbols: string[] = reel.attributes.symbols.split(',');
					for (const sym of reelSymbols) {
						this._reels[reelsIndex].push(Number.parseInt(sym, 10));
					}

					reelsIndex++;
				}
			}
		}
	}

	protected parseSymbolsPerReel(symbolsPerReel: string): void {
		// TEMP: Also set waystowin here.
		this._waysRounds = [];

		const symbols = symbolsPerReel.split(',');
		this._symbolsPerReel = [];
		let waysToWin = 1;

		for (const symbol of symbols) {
			const height: number = parseInt(symbol, 10);

			if (!isNaN(height)) {
				waysToWin *= height;
				this._symbolsPerReel.push(height);
			}
		}

		// TEMP
		this._waysRounds[0] = waysToWin;
		!__PRODUCTION__ && console.log('SymbolsPerReel:', this._symbolsPerReel);
	}

	protected parseSymbolHeights(symbolHeights: string, roundIndex: number): void {
		if (roundIndex === 0) {
			this._reelsHeight = [];
		}

		this._reelsHeight[roundIndex] = [];

		const results: any = symbolHeights.split(',');

		for (let i = 0; i < ReelConstants.REELS_AMOUNT * ReelConstants.MAX_ROW_AMOUNT; i++) {
			this._reelsHeight[roundIndex].push(-1);
		}

		for (let reel = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
			const heights = results.splice(0, this._symbolsPerReel[reel] + 1);

			for (let row = 1; row <= ReelConstants.MAX_ROW_AMOUNT; row++) {
				let element = -1;

				if (row < heights.length) {
					element = parseInt(heights[row], 10);
				}

				const realIndex = reel + 6 * (row - 1);
				this._reelsHeight[roundIndex][realIndex] = element;
			}
		}

		!__PRODUCTION__ && console.log('SymbolHeights:', roundIndex, this._reelsHeight[roundIndex]);
	}

	protected parseScatterSymbols(symbols: number[], scattersTotal: number): void {
		const scatterPositions: number[] = [];
		const symsPositions: number[] = [];

		for (let i = 0; i < symbols.length; i++) {
			if (i < scattersTotal) {
				scatterPositions.push(symbols[i]);
			} else {
				symsPositions.push(symbols[i]);
			}
		}

		this._scatterReplacements.push({scattersPositions: scatterPositions, symbolsPositions: symsPositions});
	}

	protected parseWins(data: any): void {
		if (!data.Win.length) {
			data.Win = [data.Win];
		}
		let currentWinIndex: number = 0;
		for (const win of data.Win) {
			const winReason: string = win.attributes.reason;

			if (winReason.includes('Scatter')) {
				const scattersAmountStr = winReason.substr(0, winReason.indexOf('Scatter'));
				const scattersTotal: number = Number.parseInt(scattersAmountStr, 10);
				const symbols: number[] = [];
				win.attributes.winSymbols.split(',').forEach((value) => {
					symbols.push(Number.parseInt(value, 10));
				});
				this.parseScatterSymbols(symbols, scattersTotal);
			} else if (winReason.includes('Bonus')) {
				const bonusSymbols: number[] = [];
				win.attributes.winSymbols.split(',').forEach((value) => {
					bonusSymbols.push(Number.parseInt(value, 10));
				});
				const bonusSymbolPositions: number[] = [];
				for (const bonusSym of bonusSymbols) {
					bonusSymbolPositions.push(bonusSym);
				}
				this._bonusGamesSymbols.push({symbolPositions: bonusSymbolPositions});
			} else {
				// ordinary wins
				this._wins[currentWinIndex] = {
					amount: this.parseDollar(win.attributes.amount),
					multiplier: win.attributes.multiplier ? Number.parseInt(win.attributes.multiplier, 10) : 1,
					payableID: Number.parseInt(win.attributes.paytableId, 10),
					payline: Number.parseInt(win.attributes.payline, 10),
					reason: win.attributes.reason,
					round: Number.parseInt(win.attributes.round, 10) + 1, // TODO: check this with ReCode
					ways: Number.parseInt(win.attributes.ways, 10),
					winSymbols: [],
				};
				win.attributes.winSymbols.split(',').forEach((value) => {
					this._wins[currentWinIndex].winSymbols.push(Number.parseInt(value, 10));
				});

				if (win.attributes.round === '0') {
					// TODO: terrible hack!!!
					currentWinIndex++;
					this._wins[currentWinIndex] = {
						amount: this.parseDollar(win.attributes.amount),
						multiplier: win.attributes.multiplier ? Number.parseInt(win.attributes.multiplier, 10) : 1,
						payableID: Number.parseInt(win.attributes.paytableId, 10),
						payline: Number.parseInt(win.attributes.payline, 10),
						reason: win.attributes.reason,
						round: Number.parseInt(win.attributes.round, 10),
						ways: Number.parseInt(win.attributes.ways, 10),
						winSymbols: [],
					};
					win.attributes.winSymbols.split(',').forEach((value) => {
						this._wins[currentWinIndex].winSymbols.push(Number.parseInt(value, 10));
					});
				}

				if (!__PRODUCTION__) {
					console.warn('Wins:', this._wins[currentWinIndex]);
				}
				currentWinIndex++;
			}
		}
	}

	protected parseTokens(data: any): void {
		let tokens: any = data;

		if (!tokens.length) {
			tokens = [tokens];
		}

		for (const token of tokens) {
			for (const attribute of token.Token) {
				if (attribute && attribute.attributes && attribute.attributes.name === 'WildMeter' && attribute.attributes.value) {
					this._wildMeter = Number.parseInt(attribute.attributes.value, 10);
				}
			}
		}
	}

	protected prepareInGameProgressValues(): void {
		this._roundTypes = [];
		this._currentRound = 0;
		this._totalRounds = 0;
		this._waysRounds = [];
		this._avalanches = [];
		this._currentAvalanche = 0;
		this._currentMultiplier = 1;
		this._expandedWildsMultipliers = [];
		this._baseWildsMultipliers = [];
		this._currentWildExpansion = 0;
		this._currentMultiplierWilds = 0;
		this._wildMeterDeltas = [];
	}

	protected tempInitSymbolSizes(): void {
		this._reelsHeight = [];

		this._reelsHeight[this._currentRound] = [
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
			-1,
		];
	}

	protected parseInGameProgress(data: any): void {
		const progress: any[] = data.Progress;
		const roundData: any = [];

		for (const elem of progress) {
			let name: string = elem.attributes.name;
			const value: string = elem.attributes.value;
			const splitName = name.split('_');
			let round = -1;

			if (splitName.length > 1) {
				name = splitName[0];
				round = parseInt(splitName[1], 10);
			}

			if (round > -1 && !roundData[round]) {
				roundData[round] = {};
			}

			switch (name) {
				////////////////////////////////////////////////////////////////////
				// Spin based data.
				////////////////////////////////////////////////////////////////////
				case 'RoundTypes':
					const roundTypes: string[] = value.split(',');
					roundTypes.forEach((value) => {
						this._roundTypes.push(Number.parseInt(value, 10));
					});
					break;
				case 'WildExpansions':
					const wildExpRounds: string[] = value.split('|');
					let wildIndex: number = 0;
					wildExpRounds.forEach((value) => {
						const wildExpansions: string[] = value.split(',');
						this._expandedWildsMultipliers[wildIndex] = {multipliers: []};
						wildExpansions.forEach((value) => {
							this._expandedWildsMultipliers[wildIndex].multipliers.push(Number.parseInt(value, 10));
						});
						wildIndex++;
					});
					break;
				case 'Avalanches':
					const avalanchesRounds: string[] = value.split('|');
					let avalancheIndex: number = 0;
					avalanchesRounds.forEach((value) => {
						const avalanchePositions: string[] = value.split(',');
						this._avalanches[avalancheIndex] = {symbolIndexes: []};
						avalanchePositions.forEach((value) => {
							this._avalanches[avalancheIndex].symbolIndexes.push(Number.parseInt(value, 10));
						});
						avalancheIndex++;
					});
					break;
				case 'Ways':
					const waysNumbers: string[] = value.split(',');
					waysNumbers.forEach((value) => {
						this._waysRounds.push(Number.parseInt(value, 10));
					});
					break;
				case 'SymbolsPerReel':
					this.parseSymbolsPerReel(value);
					break;

				////////////////////////////////////////////////////////////////////
				// Round based parsing.
				////////////////////////////////////////////////////////////////////
				case 'FreeSpinsAwardedSymbols':
					// console.log('FrespinsAwardedSymbols:', round, value);
					break;
				case 'WildMultipliers':
				case 'SymbolsHeight':
				case 'ScatterToRemove':
				case 'WildMeterDeltas': {
					const wildMeterDeltas: string[] = value.split(',');
					wildMeterDeltas.forEach((value) => {
						this._wildMeterDeltas.push(Number.parseInt(value, 10));
					});
					break;
				}
				case 'SymbolsToCollapse':
					roundData[round][name] = {name, value};
					break;

				default:
					break;
			}
		}

		for (let index = 0; index < roundData.length; index++) {
			this.parseRoundInGameProgressData(index, roundData[index]);
		}

		if (!__PRODUCTION__) {
			for (const round of this._roundTypes) {
				console.log('Round', round, RoundTypes[round]);
			}
			console.warn(roundData);
		}

		this._totalRounds = this._roundTypes.length;
	}

	protected parseRoundInGameProgressData(round: number, roundData: any): void {
		if (
			this._expandedWildsMultipliers.length < 1 &&
			roundData.WildMultipliers &&
			roundData.WildMultipliers.value &&
			this._roundTypes[round] !== RoundTypes.SCATTER_REPLACEMENT &&
			this._roundTypes[round] !== RoundTypes.WILD_EXPANSION
		) {
			const stringArray = roundData.WildMultipliers.value.split(',');
			const wilds: number[] = [];

			const wildsIndex = this._baseWildsMultipliers.length;

			this._baseWildsMultipliers[round + 1] = {multipliers: [], symbolIndexes: []};

			for (const str of stringArray) {
				wilds.push(Number.parseInt(str, 10));
			}

			wilds.shift(); // First element only dictates how many there are.

			for (let i = 0; i < wilds.length; i += 2) {
				this._baseWildsMultipliers[round + 1].symbolIndexes.push(wilds[i]);
				this._baseWildsMultipliers[round + 1].multipliers.push(wilds[i + 1]);
			}

			this._baseWildsMultipliers.push();

			// console.log('WildMultipliers:', round, roundData.WildMultipliers.value, this._baseWildsMultipliers);
		}

		if (roundData.SymbolsHeight && roundData.SymbolsHeight.value) {
			this.parseSymbolHeights(roundData.SymbolsHeight.value, round);
		}

		if (roundData.ScatterToRemove && roundData.ScatterToRemove.value) {
			!__PRODUCTION__ && console.log('ScatterToRemove:', round, roundData.ScatterToRemove.value);

			const symbols = [];
			roundData.ScatterToRemove.value.split(',').forEach((value) => {
				const val = Number.parseInt(value, 10);
				if (!isNaN(val)) {
					symbols.push(val);
				}
			});

			const scattersyms = [];

			this._reels[round].filter((item, index) => {
				if (item === 1) {
					scattersyms.push(index);
					symbols.unshift(index);

					return item === 1;
				}
			});

			const symbolsTotal = scattersyms.length;

			!__PRODUCTION__ && console.log(this._reels[round]);
			roundData.ScatterToRemove.scatterSymbols = scattersyms;

			this.parseScatterSymbols(symbols, symbolsTotal);
		}

		if (
			roundData.SymbolsToCollapse &&
			roundData.SymbolsToCollapse.value &&
			this._roundTypes[round + 1] !== RoundTypes.SCATTER_REPLACEMENT
		) {
			if (this._roundTypes[round + 1] === RoundTypes.SCATTER_REPLACEMENT_AND_AVALANCHE) {
				// value = value.replace()
				roundData.SymbolsToCollapse.value = roundData.SymbolsToCollapse.value.replace(`,${roundData.ScatterToRemove.value}`, '');
				for (const scatter of roundData.ScatterToRemove.scatterSymbols) {
					roundData.SymbolsToCollapse.value = (roundData.SymbolsToCollapse.value as string).substring(
						scatter.toString().length + 1,
					);
				}
			}

			const avalanchePositions: string[] = roundData.SymbolsToCollapse.value.split(',');

			const avalancheIndex = this._avalanches.length === 0 ? 0 : this._avalanches.length;

			this._avalanches[avalancheIndex] = {symbolIndexes: []};

			avalanchePositions.forEach((value) => {
				const number = Number.parseInt(value, 10);
				if (!isNaN(number)) {
					this._avalanches[avalancheIndex].symbolIndexes.push(number);
				}
			});

			!__PRODUCTION__ && console.log('SymbolsToCollapse:', round, roundData.SymbolsToCollapse.value, this._avalanches);
		}
	}

	protected setResultsForCurrentRound(): void {
		if (this._currentRound > 0 && this._reels[this._currentRound - 1]) {
			this.getRecodeSpinResults().setPreviousReels(this._reels[this._currentRound - 1], this._reelsHeight[this._currentRound - 1]);
		}

		this.getRecodeSpinResults().setWaysToWin(this._waysRounds[0]); // It is the same for all rounds in this Medallion.
		// this.getRecodeSpinResults().setWaysToWin(this._waysRounds[this._currentRound]);
		this.getRecodeSpinResults().setReels(this._reels[this._currentRound], this._reelsHeight[this._currentRound]);
		const wins: Win[] = this.getWinsFromRound(this._currentRound);
		this.getRecodeSpinResults().setWins(wins);
		this.getRecodeSpinResults().setRoundWin(this.getWinsSum(this.getWinsFromRound(this._currentRound)));
		this.getRecodeSpinResults().setNextRoundWin(this.getWinsSum(this.getWinsFromRound(this._currentRound + 1)));
		this.getRecodeSpinResults().setTotalWin(this._totalWin);

		/*	if (wins && wins.length > 0 && this.isFreeSpinsStarted) {
			this._freeSpinsMultiplier = wins[0].multiplier + 1;
		}*/

		this.setNextAction();
		// TODO: change current round values here
	}

	protected updateFreeSpinsMultipier(): void {
		this._freeSpinsPreviousMultiplier += this.isMultiplierIncreaseRound() ? 1 : 0;
	}

	protected isMultiplierIncreaseRound(): boolean {
		return (
			this.getCurrentRoundType() === RoundTypes.SCATTER_REPLACEMENT ||
			this.getCurrentRoundType() === RoundTypes.AVALANCHE ||
			this.getCurrentRoundType() === RoundTypes.SCATTER_REPLACEMENT_AND_AVALANCHE
		);
	}

	protected setResultFromInit(): void {
		this.getRecodeSpinResults().setRoundWin(this._win);
	}

	protected setNextAction(): void {
		this._previousAction = this._nextAction;
		switch (this.getNextRoundType()) {
			case RoundTypes.SCATTER_REPLACEMENT:
				this._nextAction = Action.RESPIN;
				break;
			case RoundTypes.AVALANCHE:
				this._nextAction = Action.AVALANCHE;
				break;
			case RoundTypes.SCATTER_REPLACEMENT_AND_AVALANCHE:
				this._nextAction = Action.SCATTER_AND_AVALANCHE;
				break;
			// case RoundTypes.BONUS:
			// 	this._nextAction = Action.PICK;
			// 	break;
			// case RoundTypes.FREESPIN:
			// 	this._nextAction = Action.FREESPIN;
			// 	break;
			default:
				this._nextAction = this.freeSpinsLeft > 0 ? Action.FREESPIN : Action.SPIN;
				break;
		}
	}

	protected setInitResults(): void {
		this.getRecodeSpinResults().setReels(this._reels[this._currentRound], this._reelsHeight[this._currentRound]);
		this.setNextAction();

		// if (this.getNextRoundType() === RoundTypes.BONUS) {
		// 	this.makeNextBonusSymbolsList();
		// }
		if (this.freeSpinsLeft > 0) {
			// this.makeNextFreespin!
		}
	}

	protected getWinsFromRound(roundIndex: number): Win[] {
		const wins: Win[] = [];

		for (const win of this._wins) {
			if (win.round === roundIndex) {
				wins.push(win);
			}
		}

		return wins;
	}

	protected getMultiplierForRound(roundIndex: number): number {
		return this.getWinsFromRound(roundIndex)[0].multiplier;
	}

	protected getWinsSum(wins: Win[]): number {
		let totalWin: number = 0;
		for (const win of wins) {
			totalWin += win.amount;
		}

		return totalWin;
	}

	public get sessionID(): string {
		return this._sessionID;
	}

	/**
	 * creates SymbolPosition from recode symbol position
	 */
	protected makeSymbolPositionFromSymbol(symbol: number): SymbolPosition {
		const reelPos = symbol % ReelConstants.REELS_AMOUNT;
		const rowPos = Math.trunc(symbol / ReelConstants.REELS_AMOUNT);

		return {x: reelPos, y: rowPos};
	}

	protected isWildInGameListOfExpandedWilds(reelIndex: number): boolean {
		for (const wild of this._spin.expandingWilds) {
			if (wild.reelIndex === reelIndex) {
				return true;
			}
		}

		return false;
	}

	protected findWildPositionOnReel(reelIndex: number): number {
		// const reel: GameSymbol[] = this._spin.reels[reelIndex];
		const reel: GameSymbol[] = this._spin.previousReels[reelIndex];
		for (let i = 0; i < reel.length; i++) {
			const sym: GameSymbol = reel[i];
			if (sym.sym === this.getWildSymbol()) {
				return i;
			}
		}

		return -1;
	}

	protected isWildAlreadyExpanded(reelIndex: number): boolean {
		for (const expWildReel of this._alreadyExpandedWilds) {
			if (expWildReel === reelIndex) {
				return true;
			}
		}

		return false;
	}

	public get currentFreeGameProgress(): FreeGamesInProgress {
		return this._currentFreeGameProgress;
	}

	public get lastRealWager(): FreeGamesBetData {
		return this._lastRealWager;
	}

	public set lastRealWager(betData: FreeGamesBetData) {
		this._lastRealWager = betData;
	}

	public get isInFreeRounds(): boolean {
		return this._isInFreeRounds;
	}

	public get hasExpiredFreeGames(): boolean {
		return this._hasExpiredFreeGames;
	}

	private setSpin(newSpin: SpinResult): void {
		this._spin = newSpin;
	}

	public get currentBalance(): number {
		return this._balance * RecodeLogic.CENT_TO_A_DOLLAR;
	}

	public get currentBalanceCents(): number {
		return this._balance;
	}

	public get transNumber(): string {
		return this._transNumber;
	}

	public get hasClearedExpiredFreeGames(): boolean {
		return this._hasClearedExpiredFreeGames;
	}

	public set hasClearedExpiredFreeGames(bool: boolean) {
		this._hasClearedExpiredFreeGames = bool;
	}

	public set betValue(betvalue: number) {
		this._betValue = betvalue;

		// if (this.betValues && this.betValues.length > 1) {
		// 	this._betValue = this.isValidBetValue(betvalue) ? betvalue : this.getLowerBet(betvalue);
		// }
	}

	public get betValue(): number {
		return this._betValue;
	}

	public get hasBonusGameFreeGamesRestore(): boolean {
		return this._hasBonusGameFreeGamesRestore;
	}

	public set freeGamesBonusGameRestoreCallback(value: Function) {
		this._freeGamesBonusGameRestoreCallback = value;
	}

	public get bonusGameRemainingBoxes(): number[] {
		return this._bonusGame.remaining;
	}

	public get recodeBonusGame(): BonusGameData {
		return this._bonusGame;
	}

	public get bonusSymbolsToChoose(): number[] {
		return this._bonusSymbolsToChoose;
	}

	public get serverTime(): string {
		return this._serverTime;
	}

	public get freeGamesData(): FreeGamesData[] {
		return this._freeGamesData;
	}

	public get gameState(): number {
		return this._gameState;
	}

	public get isRestoreAfterBonusGame(): boolean {
		return this._isRestoreAfterBonusGame;
	}

	public set hasKilledFreeGamesRestore(value: boolean) {
		this._hasKilledFreeGamesRestore = value;
	}

	public get hasKilledFreeGamesRestore(): boolean {
		return this._hasKilledFreeGamesRestore;
	}

	public get restoredIntoBonusGame(): boolean {
		return this._restoredIntoBonusGame;
	}

	public set restoredIntoBonusGame(bool: boolean) {
		this._restoredIntoBonusGame = bool;
	}

	public get recodeAutoplayExtraData(): IRecodeAutoPlayExtraConfig {
		return this._recodeAutoplayExtraData;
	}

	public get paylines(): number {
		return this._paylines;
	}

	public get multiplier(): number {
		return this._multiplier;
	}

	public get reels(): number[][] {
		return this._reels;
	}

	public get roundTypes(): RoundTypes[] {
		return this._roundTypes;
	}

	public get avalanches(): Avalanche[] {
		return this._avalanches;
	}

	public get wildMeterDeltas(): number[] {
		return this._wildMeterDeltas;
	}

	public getTotalWildMeterDeltas(): number {
		let total: number = 0;
		for (const delta of this._wildMeterDeltas) {
			total += delta;
		}

		return total;
	}

	public get wildMeter(): number {
		return this._wildMeter;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/integration/RecodeLogic.ts